import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { SearchInput } from '@/components/ui/search-input';
import { useIsAdmin } from '@/api/hooks';
import { ROUTES, companyPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Building blocks for the Sophos and UniFi pages. The two vendors have separate pages and data; only
 * these presentation pieces are shared. Status chips always carry a label (never colour alone).
 */

export type Tone = 'good' | 'warn' | 'bad' | 'muted' | 'info';
const TONE: Record<Tone, string> = {
  good: 'bg-success-soft text-success', warn: 'bg-warning-soft text-warning', bad: 'bg-destructive/10 text-destructive', muted: 'bg-muted text-muted-foreground', info: 'bg-info-soft text-info',
};

export function Chip({ tone, children, title }: { tone: Tone; children: React.ReactNode; title?: string }) {
  return <span title={title} className={cn('inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold', TONE[tone])}><span className="h-1.5 w-1.5 rounded-full bg-current" />{children}</span>;
}

export function CompanyCell({ companyId, companyName, fallback }: { companyId: number | null; companyName: string | null; fallback?: string | null }) {
  if (companyId != null) return <Link to={companyPath(companyId)} className="block truncate text-foreground hover:underline" title={companyName ?? ''}>{companyName}</Link>;
  return <span className="block truncate pr-0.5 text-muted-foreground" title="Not mapped to an Autotask company — Admin → Network → Mapping">{fallback ?? '—'} <span className="italic">· unmapped</span></span>;
}

export function subscriptionChip(state: string, daysLeft: number | null, end: string | null) {
  if (state === 'none') return <Chip tone="muted">No subscription</Chip>;
  if (state === 'expired') return <Chip tone="bad" title={`Ended ${end}`}>Expired {daysLeft != null ? `${-daysLeft}d ago` : ''}</Chip>;
  if (state === 'expiring') return <Chip tone="warn" title={`Ends ${end}`}>{daysLeft}d left</Chip>;
  return <span className="text-xs tabular-nums text-muted-foreground" title="Subscription end">{end}</span>;
}

/** A small info banner. */
export function Notice({ children, tone = 'info' }: { children: React.ReactNode; tone?: 'info' | 'warn' }) {
  return <div className={cn('rounded-md border px-3 py-2 text-xs', tone === 'warn' ? 'border-warning/30 bg-warning-soft text-warning' : 'border-info/30 bg-info-soft text-info')}>{children}</div>;
}

/** "N records aren't linked to a company — map them" (admin link). */
export function UnmappedNotice({ count, what }: { count: number; what: string }) {
  const { data: isAdmin } = useIsAdmin();
  if (!count) return null;
  return (
    <Notice>
      {count} {what} {count === 1 ? "isn't" : "aren't"} linked to an Autotask company yet
      {isAdmin ? <> — <Link to={ROUTES.admin.network} className="font-medium underline">map them</Link></> : ' — ask an admin to map them'}.
    </Notice>
  );
}

export function PageLoading({ isLoading, error, configured, vendor }: { isLoading: boolean; error: unknown; configured?: boolean; vendor: string }) {
  if (isLoading) return <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading {vendor}…</div>;
  if (error) return <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error instanceof Error ? error.message : 'Failed to load'}</div>;
  if (configured === false) return <div className="mt-4"><Notice tone="warn">{vendor} isn't configured on this server.</Notice></div>;
  return null;
}

// -------- List pages --------

export interface FilterDef<T> { id: string; label: string; test: (x: T) => boolean }

/**
 * Filter chips + search for a list. `?show=` (from dashboard tiles / menu views) is applied once then
 * dropped from the URL; the chosen filter persists under `key`.
 */
export function useListState<T>(key: string, filters: FilterDef<T>[], items: T[], text: (x: T) => string) {
  const [params, setParams] = useSearchParams();
  const [show, setShow] = useState<string>(() => { try { return localStorage.getItem(key) || filters[0].id; } catch { return filters[0].id; } });
  const [q, setQ] = useState('');
  const pick = (s: string) => { setShow(s); try { localStorage.setItem(key, s); } catch { /* storage unavailable */ } };
  useEffect(() => {
    const s = params.get('show');
    if (!s) return;
    if (filters.some((f) => f.id === s)) pick(s);
    setParams({}, { replace: true });
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  const counts = useMemo(() => Object.fromEntries(filters.map((f) => [f.id, items.filter(f.test).length])) as Record<string, number>, [items, filters]);
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const f = filters.find((x) => x.id === show) ?? filters[0];
    return items.filter(f.test).filter((x) => !s || text(x).toLowerCase().includes(s));
  }, [items, show, q, filters, text]);
  return { show, pick, q, setQ, counts, rows };
}

export function ListPage({ title, subtitle, count, isFetching, search, filters, actions, area = 'Network', children }: {
  title: string; subtitle?: React.ReactNode; count: number | null; isFetching: boolean;
  /** Extra controls beside the search box. */
  actions?: React.ReactNode;
  /** Names the area in the small-screen message (the kit is shared by Network and Docs). */
  area?: string;
  search?: { q: string; setQ: (s: string) => void; placeholder: string };
  filters?: { defs: { id: string; label: string }[]; show: string; pick: (s: string) => void; counts: Record<string, number> };
  children: React.ReactNode;
}) {
  return (
    <DesktopOnly title={`${area} is available on desktop`} message={`Open Cosmos on a larger screen to view ${area}.`}>
      <div className="mx-auto w-full max-w-[1600px] px-4 py-5 md:px-6">
        <PageHeader title={title} count={count} isFetching={isFetching} subtitle={subtitle}
          actions={(search || actions) && <div className="flex items-center gap-2">{actions}{search && <SearchInput withIcon placeholder={search.placeholder} className="w-72" value={search.q} onValueChange={search.setQ} />}</div>} />
        {filters && (
          // One row: a long chip list scrolls sideways instead of dropping its last chip onto a line of its own.
          <div className="mt-3 flex max-w-full gap-1 overflow-x-auto rounded-lg bg-muted p-1 [scrollbar-width:thin]" style={{ width: 'fit-content' }}>
            {filters.defs.map((f) => (
              <button key={f.id} type="button" onClick={() => filters.pick(f.id)}
                className={cn('shrink-0 whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium 2xl:px-3', filters.show === f.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                {f.label} <span className="ml-1 tabular-nums text-muted-foreground">{filters.counts[f.id] ?? 0}</span>
              </button>
            ))}
          </div>
        )}
        {children}
      </div>
    </DesktopOnly>
  );
}

export const Th = ({ children, right }: { children?: React.ReactNode; right?: boolean }) => <th className={cn('whitespace-nowrap px-3 py-2 font-medium', right && 'text-right')}>{children}</th>;
export const tableWrap = 'mt-3 overflow-x-auto rounded-lg border border-border bg-card shadow-sm';
export const theadCls = 'bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground';

export function Empty({ cols, text = 'Nothing matches.' }: { cols: number; text?: string }) {
  return <tr><td colSpan={cols} className="px-3 py-10 text-center text-muted-foreground">{text}</td></tr>;
}

export function Panel({ title, more, children }: { title: string; more?: { to: string; label: string }; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-card shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-divider px-4 py-2.5">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
        {more && <Link to={more.to} className="text-xs text-muted-foreground hover:text-foreground hover:underline">{more.label} →</Link>}
      </div>
      <div className="max-h-[520px] overflow-y-auto">{children}</div>
    </section>
  );
}
